import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { L } from '@lib';
import { InvoicePayment, TransactionType } from '@services';
import { Utils } from '@utils';
import {
  Alert,
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Typography,
} from 'antd';
import {
  BankOutlined,
  PoundOutlined,
  PoundCircleOutlined,
  CreditCardOutlined,
  InfoCircleFilled,
  ExclamationCircleFilled,
  PercentageOutlined,
} from '@ant-design/icons';
import { usePaymentTerminal, useCardPayment } from '@hooks';
import { PaidOnlyTag, SuccessButton } from '@components';

const { Text } = Typography;

type PaymentMethod = 'Card' | 'Cash' | 'Bank' | 'Other';

// Method buttons: the chosen one is solid green; the rest keep a quiet tone of their own - Card
// and Cash a light green (money taken here), Bank transfer and Other grey (money logged after
// the fact, reconciled).
const SELECTED_GREEN = '#0a7d36';

const methodButtonStyle = (
  method: PaymentMethod,
  selected: boolean,
  disabled: boolean
): React.CSSProperties => {
  const base: React.CSSProperties = {
    width: '100%',
    fontWeight: 600,
    transition: 'background-color 0.15s, border-color 0.15s, color 0.15s',
  };
  if (disabled) {
    return {
      ...base,
      background: '#f5f5f5',
      borderColor: '#d9d9d9',
      color: 'rgba(0, 0, 0, 0.25)',
      cursor: 'not-allowed',
    };
  }
  if (selected) {
    return { ...base, background: SELECTED_GREEN, borderColor: SELECTED_GREEN, color: '#fff' };
  }
  if (method === 'Card' || method === 'Cash') {
    return { ...base, background: '#e9fbe3', borderColor: '#b7e4a6', color: SELECTED_GREEN };
  }
  return { ...base, background: '#f0f1f3', borderColor: '#d9dbe0', color: '#262626' };
};

interface IProps {
  paymentForm?: FormInstance<any>;
  modalType?: 'payment' | 'refund' | null;
  invoiceTotal: number;
  totalDue: number;
  paymentTotal?: number;
  handlePaymentCreate?: (values: InvoicePayment) => Promise<void>;
  handleCashRefundCreate?: (values: InvoicePayment) => Promise<void>;
  handlePosPayment?: (values: InvoicePayment) => Promise<void>;
  handlePosRefund?: (values: InvoicePayment) => Promise<void>;
  handlePaymentCancel?: () => void;
  handlePartialPaymentInput?: (value: number) => void;
}

export const InvoicePaymentModal: React.FC<IProps> = observer(
  ({
    paymentForm,
    modalType,
    invoiceTotal,
    totalDue,
    paymentTotal,
    handlePaymentCreate,
    handleCashRefundCreate,
    handlePosPayment,
    handlePosRefund,
    handlePaymentCancel,
    handlePartialPaymentInput,
  }) => {
    const { isTerminalEnabled } = usePaymentTerminal();
    const {
      processingPayment,
      processingStatus,
      cashPaymentLoading,
      cardPaymentLoading,
      hasPaymentError,
      signatureRequired,
      trackingId,
      verifySignature,
    } = useCardPayment();

    const isOpen = modalType !== null;
    const isPaymentMode = modalType === 'payment';
    const isRefundMode = modalType === 'refund';

    const isNegativeBalanceRefund = isRefundMode && totalDue < 0;
    const dueAmount = Utils.roundToTwoDecimals(totalDue);
    const [verifyingSignature, setVerifyingSignature] = useState<'yes' | 'no' | null>(null);
    const [method, setMethod] = useState<PaymentMethod>('Cash');
    const [submitting, setSubmitting] = useState(false);

    const isSubmittingRef = useRef(false);

    const cardBusy = cardPaymentLoading && processingPayment;
    const busy = cardBusy || cashPaymentLoading || submitting;
    // Card needs a terminal; Bank transfer and Other are only ever payments, never refunds.
    const cardUnavailable = !isTerminalEnabled;
    const isLoggedMethod = method === 'Bank' || method === 'Other';

    // Opens on Card when there is a terminal to take it on, otherwise on Cash.
    useEffect(() => {
      if (isOpen) setMethod(isTerminalEnabled ? 'Card' : 'Cash');
    }, [isOpen, isTerminalEnabled]);

    const getModalTitle = () => {
      switch (modalType) {
        case 'payment':
          return L('Take payment');
        case 'refund':
          return L('Process refund');
        default:
          return L('Payment');
      }
    };

    // Dynamic validation based on modal type
    const getAmountValidation = () => {
      // ✅ Skip validation for negative balance refunds (readonly field)
      if (isNegativeBalanceRefund) {
        return [];
      }

      const baseRules = [
        { required: true, message: L('Please enter amount') },
        {
          validator: (_: any, value: number) => {
            if (value === null || value === undefined) {
              return Promise.reject(L('Please enter amount'));
            }

            if (value <= 0) {
              return Promise.reject(L('Amount must be greater than 0'));
            }

            if (isPaymentMode) {
              if (value > dueAmount) return Promise.reject(L('Amount cannot exceed total due'));
              if (value > invoiceTotal)
                return Promise.reject(L('Amount cannot exceed total invoice amount'));
            }

            if (isRefundMode) {
              if (value > (paymentTotal || 0)) {
                return Promise.reject(L('Refund amount cannot exceed paid amount'));
              }
            }

            return Promise.resolve();
          },
        },
      ];
      return baseRules;
    };

    // Dynamic max amount based on modal type
    const getMaxAmount = () => {
      // ✅ For negative balance refunds, use absolute value of totalDue
      if (isNegativeBalanceRefund) {
        return Utils.roundToTwoDecimals(Math.abs(totalDue));
      }

      if (isPaymentMode) return Utils.roundToTwoDecimals(Math.min(dueAmount, invoiceTotal));
      //if (isRefundMode) return Utils.roundToTwoDecimals(paymentTotal || 0);
      if (isRefundMode) return Utils.roundToTwoDecimals(totalDue);
      return Utils.roundToTwoDecimals(invoiceTotal);
    };

    const handleSignatureVerification = useCallback(
      async (isValid: boolean) => {
        if (trackingId) {
          try {
            setVerifyingSignature(isValid ? 'yes' : 'no');
            await verifySignature(trackingId, isValid);
          } catch (error) {
            setVerifyingSignature(null);
          } finally {
            setVerifyingSignature(null);
          }
        }
      },
      [trackingId, verifySignature]
    );

    // Dynamic form initial values based on modal type
    useEffect(() => {
      if (isOpen && paymentForm) {
        let defaultAmount = 0;

        if (isPaymentMode && dueAmount > 0) {
          defaultAmount = dueAmount;
        } else if (isRefundMode) {
          //defaultAmount = totalDue;
          // ✅ Use totalDue for negative balance refunds, otherwise use paymentTotal
          defaultAmount = isNegativeBalanceRefund ? Math.abs(totalDue) : paymentTotal || 0;
        } //else if (isCreditMode && dueAmount > 0) {
        //defaultAmount = dueAmount;
        //}

        const roundedAmount = Utils.roundToTwoDecimals(defaultAmount);

        paymentForm.setFieldsValue({
          amount: roundedAmount,
          note: '',
        });
      }
    }, [
      isOpen,
      paymentForm,
      dueAmount,
      paymentTotal,
      modalType,
      totalDue,
      isNegativeBalanceRefund,
    ]);

    const handleCashPayment = async () => {
      const values = await paymentForm?.validateFields();
      if (!values) return;

      const paymentData = {
        ...values,
        method: isRefundMode ? 'CashRefund' : 'Cash',
        type: modalType,
      };

      if (isRefundMode) {
        await handleCashRefundCreate?.(paymentData);
      } else {
        await handlePaymentCreate?.(paymentData);
      }
    };

    const handleCardPayment = async () => {
      const values = await paymentForm?.validateFields();
      if (!values) return;

      const paymentData = {
        ...values,
        method: isRefundMode ? 'CardRefund' : 'Card',
        type: modalType,
      };

      if (isRefundMode) {
        await handlePosRefund?.(paymentData);
      } else {
        await handlePosPayment?.(paymentData);
      }
    };

    // Money that has already changed hands (a bank transfer, anything else): logged as paid and
    // reconciled, never sent to the terminal.
    const handleLoggedPayment = async () => {
      const values = await paymentForm?.validateFields();
      if (!values) return;

      await handlePaymentCreate?.({
        ...values,
        method: method === 'Bank' ? 'BankTransfer' : 'Other',
        transactionType:
          method === 'Bank' ? TransactionType.BankTransfer : TransactionType.OtherPayment,
        reconciled: true,
        type: 'payment',
      });
    };

    const handleRecord = async () => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setSubmitting(true);
      try {
        if (method === 'Card') await handleCardPayment();
        else if (method === 'Cash') await handleCashPayment();
        else await handleLoggedPayment();
      } catch (error) {
        console.error('Payment failed:', error);
      } finally {
        isSubmittingRef.current = false;
        setSubmitting(false);
      }
    };

    // Dynamic amount label
    const getAmountLabel = () => {
      if (isRefundMode) return L('Refund Amount');
      return L('Payment Amount');
    };

    const confirmTitle = () => {
      const card = method === 'Card';
      if (isRefundMode) return card ? 'Process card refund?' : 'Process cash refund?';
      return card ? 'Process card payment?' : 'Process cash payment?';
    };

    const confirmDescription = () => {
      const card = method === 'Card';
      if (isRefundMode) {
        return card
          ? 'This will initiate a refund to the card terminal.'
          : 'This will record a cash refund for this invoice.';
      }
      return card
        ? 'This will send the payment to the card terminal.'
        : 'This will record a cash payment for this invoice.';
    };

    const statusMessageComponent = useMemo(() => {
      if (!processingStatus?.terminalMessage) return null;

      return (
        <>
          <Alert message={`EPOS: ${processingStatus.terminalMessage}`} type="info" showIcon />
          <br />
        </>
      );
    }, [processingStatus?.terminalMessage]);

    const eposStatusComponent = useMemo(() => {
      if (!signatureRequired) {
        return (
          <Alert
            type="info"
            message={L(`Card ${modalType} is being processed`)}
            description={
              <>
                <div>
                  {L('Please wait while the transaction completes. Do not close this window.')}
                </div>
                {processingStatus?.statusMessage}
              </>
            }
            showIcon
          />
        );
      }

      return (
        <Alert
          type="warning"
          message={
            <div style={{ textAlign: 'center' }}>{L('Signature Verification Required')}</div>
          }
          description={
            <>
              <Row justify="center" style={{ marginBottom: 16 }}>
                <Col span={24} style={{ textAlign: 'center' }}>
                  <Text strong>{L('Is the Signature on the Receipt Valid?')}</Text>
                </Col>
              </Row>
              <Row justify="center" gutter={12}>
                <Col span={8}>
                  <SuccessButton
                    size="large"
                    block
                    onClick={() => handleSignatureVerification(true)}
                    loading={verifyingSignature === 'yes'}
                    disabled={verifyingSignature !== null}
                  >
                    {L('Yes')}
                  </SuccessButton>
                </Col>
                <Col span={8}>
                  <Button
                    type="primary"
                    danger
                    size="large"
                    block
                    onClick={() => handleSignatureVerification(false)}
                    loading={verifyingSignature === 'no'}
                    disabled={verifyingSignature !== null}
                  >
                    {L('No')}
                  </Button>
                </Col>
              </Row>
            </>
          }
          showIcon={false}
          closable={false}
          style={{ marginTop: 16 }}
        />
      );
    }, [
      signatureRequired,
      handleSignatureVerification,
      modalType,
      verifyingSignature,
      processingStatus?.statusMessage,
    ]);

    return (
      <Modal
        title={getModalTitle()}
        open={isOpen}
        onCancel={handlePaymentCancel}
        closable={!cardBusy}
        mask={{ closable: false }}
        destroyOnHidden
        width={520}
        footer={
          <Space>
            <Button onClick={handlePaymentCancel} disabled={cardBusy}>
              {L('Cancel')}
            </Button>
            <Popconfirm
              title={
                <span style={{ fontSize: 15, fontWeight: 600 }}>{L(confirmTitle())}</span>
              }
              description={L(confirmDescription())}
              onConfirm={handleRecord}
              okText={L('Yes, proceed')}
              cancelText={L('Cancel')}
              okButtonProps={{ size: 'middle' }}
              cancelButtonProps={{ size: 'middle' }}
              icon={<ExclamationCircleFilled style={{ color: '#faad14', fontSize: 16 }} />}
              // Card and cash are confirmed first; a logged transfer records straight away.
              disabled={isLoggedMethod || busy || (method === 'Card' && cardUnavailable)}
            >
              <Button
                type="primary"
                loading={busy}
                disabled={method === 'Card' && cardUnavailable}
                onClick={isLoggedMethod ? handleRecord : undefined}
              >
                {isRefundMode ? L('Record refund') : L('Record payment')}
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        {/* Display different summary based on modal type */}
        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            <Text>{L('Total invoice')}</Text>
          </Col>
          <Col>
            <Text strong>{Utils.formatCurrency(invoiceTotal)}</Text>
          </Col>
        </Row>

        <Row justify="space-between" style={{ marginBottom: 12 }}>
          <Col>
            <Text>{L('Total paid')}</Text>
          </Col>
          <Col>
            <Text strong>{Utils.formatCurrency(paymentTotal || 0)}</Text>
          </Col>
        </Row>

        <Row
          justify="space-between"
          style={{ marginBottom: 20, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}
        >
          <Col>
            <Text strong>{isRefundMode ? L('Available for refund') : L('Total due')}</Text>
          </Col>
          <Col>
            <Text strong style={{ color: '#2CBDFF', fontSize: 16 }}>
              {Utils.formatCurrency(
                // ✅ Show totalDue for negative balance refunds, otherwise use existing logic
                isNegativeBalanceRefund
                  ? Math.abs(totalDue)
                  : isRefundMode
                    ? paymentTotal || 0
                    : dueAmount
              )}
            </Text>
          </Col>
        </Row>

        {/* ✅ Show notice for negative balance refunds */}
        {isNegativeBalanceRefund && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Alert
                type="info"
                // message={L('Overpayment Refund')}
                description={L('Customer has requested a refund. Click to refund by')}
                showIcon
                icon={
                  <InfoCircleFilled
                    style={{
                      fontSize: 32, // Make icon bigger
                      verticalAlign: 'middle', // Center icon vertically
                      // color: '#1890ff'
                    }}
                  />
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              />
            </Col>
          </Row>
        )}

        <Form form={paymentForm} layout="vertical">
          <Form.Item
            name="amount"
            label={getAmountLabel()}
            rules={getAmountValidation()}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Input
              type="number"
              min={0.01}
              max={getMaxAmount()}
              step="0.01"
              prefix="£"
              addonAfter={<PoundOutlined />}
              disabled={isRefundMode}
              readOnly={isRefundMode}
              onChange={(e) => {
                if (isNegativeBalanceRefund) return;

                let value = parseFloat(e.target.value);

                const max = getMaxAmount();
                if (!isNaN(value) && value > max) {
                  value = max;
                  if (paymentForm) {
                    paymentForm.setFieldsValue({ amount: max });
                  }
                }

                if (isNaN(value) || e.target.value === '') {
                  paymentForm?.validateFields(['amount']);
                } else if (value === 0) {
                  paymentForm?.validateFields(['amount']);
                } else {
                  handlePartialPaymentInput?.(value);
                }
              }}
              onWheel={(e) => e.currentTarget.blur()}
              style={{
                backgroundColor: isNegativeBalanceRefund ? '#f5f5f5' : undefined,
                color: isNegativeBalanceRefund ? '#666' : undefined,
              }}
            />
          </Form.Item>

          <Form.Item label={L(isRefundMode ? 'Select refund method' : 'Select payment method')}>
            <Space vertical size={10} style={{ width: '100%' }}>
              <Button
                size="large"
                icon={<CreditCardOutlined />}
                disabled={busy || cardUnavailable}
                onClick={() => setMethod('Card')}
                style={{
                  ...methodButtonStyle('Card', method === 'Card', cardUnavailable),
                  height: 56,
                  fontSize: 18,
                }}
              >
                <Space size={8}>
                  {L('Card')}
                  <PaidOnlyTag />
                </Space>
              </Button>

              <Button
                size="large"
                icon={<PoundCircleOutlined />}
                disabled={busy}
                onClick={() => setMethod('Cash')}
                style={{
                  ...methodButtonStyle('Cash', method === 'Cash', false),
                  height: 44,
                  fontSize: 16,
                }}
              >
                {L('Cash')}
              </Button>

              {/* A refund goes back the way it came: card or cash. Transfers are only logged. */}
              {isPaymentMode && (
                <Row gutter={10}>
                  <Col span={12}>
                    <Button
                      icon={<BankOutlined />}
                      disabled={busy}
                      onClick={() => setMethod('Bank')}
                      style={{
                        ...methodButtonStyle('Bank', method === 'Bank', false),
                        height: 40,
                      }}
                    >
                      {L('Bank transfer')}
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      icon={<PercentageOutlined />}
                      disabled={busy}
                      onClick={() => setMethod('Other')}
                      style={{
                        ...methodButtonStyle('Other', method === 'Other', false),
                        height: 40,
                      }}
                    >
                      {L('Other')}
                    </Button>
                  </Col>
                </Row>
              )}
            </Space>
          </Form.Item>

          {/* The character count hangs below the field, in the form item's bottom margin - so it
              keeps that margin, or the footer sits on top of it. */}
          <Form.Item name="note" label={L('Notes (optional)')} style={{ marginBottom: 28 }}>
            <Input.TextArea rows={3} placeholder={L('Add a note...')} maxLength={100} showCount />
          </Form.Item>

          {cardBusy && (
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                {statusMessageComponent}
                {eposStatusComponent}
              </Col>
            </Row>
          )}
          {hasPaymentError && (
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                <Alert
                  type="error"
                  message={L('Processing Error')}
                  description={
                    processingStatus?.statusMessage || L('Operation failed. Please try again.')
                  }
                  showIcon
                  closable
                />
              </Col>
            </Row>
          )}
        </Form>
      </Modal>
    );
  }
);
